import {
  ECalculatorConfigKeys,
  EChatConfigKeys,
  EMainConfigKeys,
  EUserType,
  TAspectRationOptions,
  TConfigKey,
  TLayoutItems,
  TVideoControls,
  TVideoObjectFit,
} from '@src/types';

export const ASPECT_RATIO_OPTIONS: { label: TAspectRationOptions; value: number }[] = [
  { label: 'default (4:3)', value: 0.75 },
  { label: '16:9', value: 0.5625 },
  { label: '1:1', value: 1 },
  { label: '1:2', value: 2 },
];

export const VIDEO_OBJECT_FIT: TVideoObjectFit[] = ['none', 'contain', 'fill', 'scale-down'];

export const VIDEO_CONTROL_OPTIONS: TVideoControls[] = ['on', 'off'];

export const SYSTEM_LANGUAGES = [{ value: 'english', label: 'English' }];

export const SYSTEM_THEMES = [
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
];

export const LAYOUT_OPTIONS: { value: TLayoutItems; text: string; img: string }[] = [
  { value: 'TILED', text: 'Tiled (Default)', img: '../images/group32.png' },
  { value: 'SPOTLIGHT', text: 'Spotlight', img: '../images/group33.png' },
  { value: 'SIDEBAR', text: 'Side bar', img: '../images/group34.png' },
  { value: 'TOP_BAR', text: 'Top bar', img: '../images/group35.png' },
  { value: 'BOTTOM_BAR', text: 'Bottom bar', img: '../images/group36.png' },
  { value: 'INSTRUCTOR', text: 'Instructor', img: '../images/group37.png' },
];

/**
 * CONFIG_TOGGLE_MAP defines metadata for each toggleable room setting.
 *
 * Each entry maps a config key (TConfigKey) to:
 * - `section`: where the config belongs ('main' or 'chat' .. etc)
 * - `roles`: which user types (EUserType) the setting applies to (users who will be affected by the change)
 * - `prefixLabel`: label shown in the UI
 * - `shouldNotifyOnClose`: whether disabling the toggle should trigger a notification
 *
 * This map is used to:
 * - Build update payloads for the server (in `useGeneralSettings.ts`)
 * - Control visibility and access based on role
 * - Display toggle labels and notifications
 */

export const CONFIG_TOGGLE_MAP: Partial<
  Record<
    TConfigKey,
    {
      section: 'main' | 'chat' | 'calculator';
      roles: EUserType[];
      prefixLabel: string;
      shouldNotifyOnClose?: boolean;
    }
  >
> = {
  [EMainConfigKeys.ChatButton]: {
    section: 'main',
    roles: [EUserType.ATTENDEE, EUserType.HOST, EUserType.CO_HOST, EUserType.SPEAKER],
    prefixLabel: 'Public chat',
    shouldNotifyOnClose: true,
  },
  [EChatConfigKeys.PrivateChatButton]: {
    section: 'chat',
    roles: [EUserType.ATTENDEE, EUserType.HOST, EUserType.CO_HOST, EUserType.SPEAKER],
    prefixLabel: 'Private chat',
    shouldNotifyOnClose: true,
  },
  [EMainConfigKeys.PollButton]: {
    section: 'main',
    roles: [EUserType.ATTENDEE, EUserType.HOST, EUserType.CO_HOST, EUserType.SPEAKER],
    prefixLabel: 'Polls',
  },
  [EMainConfigKeys.StartScreenButton]: {
    section: 'main',
    roles: [EUserType.ATTENDEE],
    prefixLabel: 'Screen sharing',
  },
  [ECalculatorConfigKeys.calculatorShowButton]: {
    section: 'calculator',
    roles: [EUserType.ATTENDEE, EUserType.HOST, EUserType.CO_HOST, EUserType.SPEAKER],
    prefixLabel: 'Calculator visibility',
  },
  [ECalculatorConfigKeys.shareCalculatorButton]: {
    section: 'calculator',
    roles: [EUserType.ATTENDEE],
    prefixLabel: 'Calculator sharing',
  },
};

export const MEDIA_TYPE = {
  audio: 'audioType',
  audioTab: 'audioTab',
  video: 'videoType',
  camera: 'cameraType',
  screen: 'screenType',
  speaker: 'speakerType',
};
